import { useRef, useState } from "react";
import classnames from "classnames";
import { useFieldArray, useFormContext } from "react-hook-form";
import { t } from "@/i18n-js/instance";
import { IconButton } from "@circle-react-shared/uikit/HeaderV3/IconButton";
import type { DropdownOptions } from "@circle-react-uikit/Dropdown";
import { DROPDOWN_SIZE, Dropdown } from "@circle-react-uikit/Dropdown";
import { Typography } from "@circle-react-uikit/Typography";
import { StaticOptionItem } from "./StaticOptionItem";

interface QuestionsItemProps {
  firstVisibleIndex: number;
  lastVisibleIndex: number;
  onDelete: (index: number, payload: { id: number; _destroy: boolean }) => void;
  onEdit: () => void;
  onMoveDown: () => void;
  onMoveUp: () => void;
  questionIndex: number;
}
export const QuestionItem = ({
  questionIndex,
  onDelete,
  onEdit,
  onMoveUp,
  onMoveDown,
  firstVisibleIndex,
  lastVisibleIndex,
}: QuestionsItemProps) => {
  const [wrapperClassName, setWrapperClassName] = useState<string | null>(null);
  const questionRef = useRef<HTMLDivElement>(null);
  const { watch } = useFormContext();
  const question = watch(`quiz[questions][${questionIndex}]`);
  const { fields: options } = useFieldArray({
    name: `quiz[questions][${questionIndex}].options`,
  });
  const animateMovement = () => {
    setWrapperClassName("opacity-10");
    setTimeout(() => {
      questionRef.current?.scrollIntoView({ behavior: "smooth" });
      setTimeout(() => {
        setWrapperClassName(null);
      }, 700); // make the opacity animation noticeable for a while
    }, 50); // add a delay to wait for move(from, to) to re-render the components
  };

  if (question._destroy) return null;

  const dropdownOptions: DropdownOptions = [
    {
      label: t("edit"),
      onClick: onEdit,
      value: "edit",
      icon: "16-edit",
    },
    {
      label: t("courses.edit.lesson.move_up"),
      onClick: () => {
        onMoveUp();
        animateMovement();
      },
      value: "move_up",
      icon: "16-arrow-up",
      isDisabled: questionIndex === firstVisibleIndex,
    },
    {
      label: t("courses.edit.lesson.move_down"),
      onClick: () => {
        onMoveDown();
        animateMovement();
      },
      value: "move_down",
      icon: "16-arrow-down",
      isDisabled: questionIndex === lastVisibleIndex,
    },
    {
      label: t("delete"),
      onClick: () =>
        onDelete(questionIndex, { id: question.id, _destroy: true }),
      color: "text-v2-danger",
      value: "delete",
      icon: "16-trash-bin",
      iconClassName: "text-v2-danger",
    },
  ];

  return (
    <div
      ref={questionRef}
      className={classnames(
        "border-primary flex flex-col rounded-lg border p-8",
        wrapperClassName,
      )}
    >
      {question.cover_media && (
        <div className="bg-secondary group relative mb-4 aspect-[2.7388059701] w-full rounded-lg">
          <img
            src={question.cover_media.url}
            loading="lazy"
            alt={t("courses.edit.lesson.cover_media_alt")}
            className="h-full w-full rounded-lg"
          />
        </div>
      )}
      <div className="flex items-start justify-between">
        <Typography.LabelMd weight="semibold">
          {question.statement}
        </Typography.LabelMd>
        <Dropdown
          size={DROPDOWN_SIZE.MEDIUM}
          direction="bottom-end"
          linkType="button"
          button={
            <IconButton
              name="20-menu-dots-horizontal"
              iconSize={20}
              buttonSize={28}
              variant="secondary"
              ariaLabel={t("courses.edit.lesson.question_menu_options")}
            />
          }
          buttonLabel={t("courses.edit.lesson.question_menu_options")}
          options={dropdownOptions}
        />
      </div>
      {options.map((option, index) => (
        <StaticOptionItem
          key={option.id}
          questionIndex={questionIndex}
          optionIndex={index}
          questionType={question.question_type}
        />
      ))}
    </div>
  );
};
